import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { useCheckout } from "../hooks/useCheckout";
import { useCart } from "../contexts/CartContext";
import {
  setActiveStep as setActiveStepAction,
  resetCheckout,
  setError,
  setLoading,
  setClientSecret,
  setPaymentCompleted,
  setOrderId,
  setOrderNumber,
} from "../store/slices/checkoutSlice";
import checkoutService from "../services/checkoutService";
import AddressStep from "../components/checkout/AddressStep";
import PaymentStep from "../components/checkout/PaymentStep";
import ReviewStep from "../components/checkout/ReviewStep";
import OrderSummary from "../components/checkout/OrderSummary";
import { orderService } from "../services/orderService";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * Main Checkout Page Component
 * Manages the checkout flow using a stepper and renders step-specific components.
 */
const CheckoutPage = () => {
  const { t } = useTranslation();
  const { t: tCheckout } = useTranslation("checkout");
  const steps = [
    { label: tCheckout("steps.addressShipping"), component: AddressStep },
    { label: tCheckout("steps.payment"), component: PaymentStep },
    { label: tCheckout("steps.orderComplete"), component: ReviewStep },
  ];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation(); // Get access to URL parameters
  const { cart } = useCart();
  const [completedOrder, setCompletedOrder] = useState(null);
  const [fetchOrderError, setFetchOrderError] = useState(null);
  const [walletAppliedAmount, setWalletAppliedAmount] = useState(0);

  // Get state from Redux and the checkout hook
  const activeStep = useSelector((state) => state.checkout.activeStep);
  const loading = useSelector((state) => state.checkout.loading);
  const globalError = useSelector((state) => state.checkout.error);
  const checkoutData = useSelector((state) => state.checkout.checkoutData);
  const clientSecret = useSelector((state) => state.checkout.clientSecret);
  const orderNumber = useSelector((state) => state.checkout.orderNumber);
  const currentOrderId = useSelector((state) => state.checkout.orderId);
  const paymentCompleted = useSelector(
    (state) => state.checkout.paymentCompleted
  );

  console.log(
    `CheckoutPage Rendering: Initial activeStep=${activeStep}, orderId=${currentOrderId}`
  );

  // Checkout hook provides actions and Stripe promise
  const {
    createCheckoutSession,
    processPayment,
    goToConfirmation,
    stripePromise,
    stripeInitialized,
    stripeError,
    clearCart: clearCartAction,
    fetchCart,
    checkoutSession,
  } = useCheckout();

  // Check for redirect back from payment provider (e.g., TWINT)
  useEffect(() => {
    const processRedirect = async () => {
      const query = new URLSearchParams(location.search);
      const paymentIntentId = query.get("payment_intent");
      const redirectStatus = query.get("redirect_status");
      const clientSecretFromUrl = query.get("payment_intent_client_secret");

      console.log("CheckoutPage: URL parameters:", {
        paymentIntentId,
        redirectStatus,
        clientSecretFromUrl: !!clientSecretFromUrl,
        search: location.search,
      });

      // Only process if we have required params and haven't processed this redirect yet
      if (
        paymentIntentId &&
        redirectStatus &&
        activeStep !== 2 &&
        !paymentCompleted // Prevent reprocessing if already marked complete
      ) {
        console.log(
          `CheckoutPage: Detected return from payment redirect. Status: ${redirectStatus}, PI: ${paymentIntentId}`
        );

        dispatch(setLoading(true));
        dispatch(setError(null)); // Clear previous errors

        try {
          if (
            redirectStatus === "succeeded" ||
            redirectStatus === "processing"
          ) {
            console.log(
              "CheckoutPage: Payment redirect successful/processing. Fetching order details..."
            );

            // 1. Fetch order details using the paymentIntentId
            const orderDetails = await checkoutService.getOrderByPaymentIntent(
              paymentIntentId
            );

            if (
              !orderDetails ||
              !orderDetails.success ||
              !orderDetails.orderId
            ) {
              throw new Error(tCheckout("couldNotRetrieveOrderDetails"));
            }

            console.log("CheckoutPage: Retrieved order details:", orderDetails);

            // 2. Update Redux state with retrieved orderId and potentially clientSecret
            dispatch(setOrderId(orderDetails.orderId));
            dispatch(setOrderNumber(orderDetails.orderNumber)); // Update order number too
            if (clientSecretFromUrl && !clientSecret) {
              // Only set client secret from URL if not already in state
              dispatch(setClientSecret(clientSecretFromUrl));
            }

            // 3. Now process payment confirmation with the retrieved orderId
            console.log(
              `CheckoutPage: Calling processPayment with retrieved Order ID: ${orderDetails.orderId}`
            );
            const paymentResult = await processPayment({
              paymentIntentId: paymentIntentId,
              orderId: orderDetails.orderId, // Pass retrieved orderId explicitly
            });

            if (paymentResult) {
              console.log(
                "CheckoutPage: Successfully processed redirected payment."
              );
              // processPayment should handle setting step to 2 on success
            } else {
              // processPayment failed, error should be set in Redux by the hook
              console.error(
                "CheckoutPage: processPayment failed after redirect."
              );
            }
          } else {
            // Payment failed or was canceled
            console.error(
              `CheckoutPage: Payment redirect failed with status: ${redirectStatus}`
            );
            dispatch(
              setError(
                tCheckout("paymentCouldNotBeCompleted", {
                  status: redirectStatus,
                })
              )
            );
          }
        } catch (err) {
          console.error(
            "CheckoutPage: Error processing redirected payment:",
            err
          );
          dispatch(
            setError(
              err.message || tCheckout("failedToProcessPaymentAfterReturn")
            )
          );
        } finally {
          dispatch(setLoading(false));
          // Clean the URL to prevent reprocessing on refresh
          navigate(location.pathname, { replace: true });
        }
      }
    };

    processRedirect();
  }, [
    location,
    dispatch,
    activeStep,
    paymentCompleted,
    clientSecret,
    navigate,
  ]);

  // Fetch cart state and completed order details when reaching the final step
  useEffect(() => {
    if (activeStep === 2) {
      console.log(
        "CheckoutPage useEffect [Step 2]: Reached final step. Refreshing cart and fetching order..."
      );

      // Clear cart items and refresh cart state to ensure UI shows empty cart
      clearCartAction()
        .then(() => {
          // After clearing, fetch latest cart state
          fetchCart().catch((err) => {
            console.error(
              "CheckoutPage: Failed to fetch cart upon reaching step 2",
              err
            );
          });
        })
        .catch((err) => {
          console.error("CheckoutPage: Failed to clear cart", err);
        });

      // Fetch the completed order details if orderId exists
      if (currentOrderId) {
        orderService
          .getOrderById(currentOrderId)
          .then((orderData) => {
            console.log(
              "CheckoutPage: Fetched completed order details:",
              orderData
            );
            setCompletedOrder(orderData);
            setFetchOrderError(null);
          })
          .catch((err) => {
            console.error(
              `CheckoutPage: Failed to fetch completed order ${currentOrderId}`,
              err
            );
            setFetchOrderError(tCheckout("couldNotLoadOrderDetails"));
            setCompletedOrder(null);
          });
      } else {
        setFetchOrderError(tCheckout("orderIdMissing"));
        setCompletedOrder(null);
      }
    }
    // Dependencies: activeStep, orderId
  }, [activeStep, currentOrderId, dispatch, tCheckout]);

  // Log the activeStep used for rendering the content
  console.log(
    `CheckoutPage Rendering: activeStep used for getStepContent=${activeStep}`
  );

  // Step Navigation Handlers
  const handleBack = useCallback(() => {
    if (activeStep > 0) {
      dispatch(setActiveStepAction(activeStep - 1));
    } else {
      navigate("/cart");
    }
  }, [dispatch, activeStep, navigate]);

  const handleNext = useCallback(async () => {
    if (activeStep === 0) {
      console.log("CheckoutPage: Proceeding from Address to Payment...");
      try {
        await createCheckoutSession();
      } catch (err) {
        console.error("CheckoutPage: Error creating session:", err);
      }
    }
  }, [activeStep, createCheckoutSession]);

  // Form Validation
  const isAddressStepValid = useMemo(() => {
    const { shippingAddress, billingAddress } = checkoutData;
    const isShippingValid =
      shippingAddress.address &&
      shippingAddress.city &&
      shippingAddress.postalCode &&
      shippingAddress.country;

    if (billingAddress.sameAsShipping) {
      return isShippingValid;
    }

    const isBillingValid =
      billingAddress.address &&
      billingAddress.city &&
      billingAddress.postalCode &&
      billingAddress.country;

    return isShippingValid && isBillingValid;
  }, [checkoutData]);

  // Stripe Elements Options
  const stripeElementsOptions = useMemo(() => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: "#1976d2",
          colorBackground: "#ffffff",
          colorText: "#30313d",
          colorDanger: "#df1b41",
          fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
          borderRadius: "4px",
        },
      },
    };
  }, [clientSecret]);

  // Handler for Bank Transfer button click - MOVED UP BEFORE renderStepContent
  const handleBankTransferSubmit = useCallback(async () => {
    console.log("CheckoutPage: Manually confirming bank transfer order...");
    try {
      dispatch(setActiveStepAction(2)); // Properly dispatch the action
      await processPayment({ paymentMethod: "bank_transfer_manual" });
      // Navigation to confirmation page is handled by the checkout slice
    } catch (error) {
      console.error("CheckoutPage: Bank transfer submission error:", error);
    }
  }, [dispatch, processPayment]);

  // Render Step Content
  const renderStepContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <AddressStep onWalletAmountChange={setWalletAppliedAmount} />;
      case 1: // Payment Step
        // Ensure Stripe is initialized before rendering the Elements provider
        if (!stripeInitialized || !stripePromise) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              {stripeError ? (
                <Alert severity="error">{stripeError}</Alert>
              ) : (
                <CircularProgress />
              )}
              <Typography sx={{ ml: 2 }}>
                {stripeError
                  ? tCheckout("paymentSystemError")
                  : tCheckout("initializingPaymentSystem")}
              </Typography>
            </Box>
          );
        }

        // Check if fully paid by wallet from backend response
        const paidByWallet = checkoutSession?.paidByWalletOnly;

        if (paidByWallet) {
          // If fully paid by wallet, show confirmation message
          return (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CheckCircleIcon
                sx={{ fontSize: 60, mb: 2, color: "success.main" }}
              />
              <Typography variant="h6" gutterBottom>
                {tCheckout("paymentComplete")}
              </Typography>
              <Typography>{tCheckout("orderFullyPaidWallet")}</Typography>
              {/* Optional button to manually proceed */}
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => dispatch(setActiveStepAction(2))}
              >
                {tCheckout("continueToOrderConfirmation")}
              </Button>
            </Box>
          );
        }

        // If payment is needed, proceed with Stripe Elements
        // Always wrap PaymentStep with Elements provider when Stripe is initialized
        return (
          <Elements
            stripe={stripePromise}
            options={
              clientSecret
                ? {
                    clientSecret,
                    appearance: {
                      theme: "stripe",
                      variables: {
                        colorPrimary: "#1976d2",
                        colorBackground: "#ffffff",
                        colorText: "#30313d",
                        colorDanger: "#df1b41",
                        fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
                        borderRadius: "4px",
                      },
                    },
                  }
                : {}
            }
          >
            <PaymentStep
              amountDue={checkoutSession?.amountDue}
              onBankTransferSubmit={handleBankTransferSubmit}
            />
          </Elements>
        );

      case 2:
        return <ReviewStep orderNumber={orderNumber} />;
      default:
        return <Typography>{tCheckout("errorUnknownStep")}</Typography>;
    }
  }, [
    activeStep,
    stripePromise,
    clientSecret,
    stripeInitialized,
    stripeError,
    orderNumber,
    checkoutSession,
    setWalletAppliedAmount,
    handleBankTransferSubmit,
    tCheckout,
    dispatch,
  ]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Typography variant="h4" align="center" gutterBottom>
        {tCheckout("title")}
      </Typography>

      {globalError ||
        (stripeError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {globalError || stripeError}
          </Alert>
        ))}

      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {steps.map(({ label }, index) => (
          <Step key={label}>
            <StepLabel
              sx={{
                "& .MuiStepLabel-iconContainer .Mui-active": {
                  color: "white", // White color for the number when active
                },
                "& .MuiStepLabel-iconContainer .Mui-active .MuiStepIcon-text": {
                  fill: "white", // White color for the number inside the icon
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>{renderStepContent()}</Paper>

          {activeStep < 2 && (
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}
            >
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
              >
                {activeStep === 0 ? tCheckout("backToCart") : tCheckout("back")}
              </Button>

              {activeStep === 0 && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading || !isAddressStepValid}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    tCheckout("proceedToPayment")
                  )}
                </Button>
              )}

              {activeStep === 1 &&
                checkoutData.paymentMethod === "bank_transfer_manual" &&
                // This button is now handled inside the PaymentStep component
                // No external button needed here
                null}
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button variant="contained" onClick={() => navigate("/orders")}>
                {tCheckout("viewMyOrders")}
              </Button>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <OrderSummary
            cart={activeStep < 2 ? cart : null}
            order={activeStep === 2 ? completedOrder : null}
            checkoutData={checkoutData}
            isLoading={activeStep === 2 && !completedOrder && !fetchOrderError}
            error={activeStep === 2 ? fetchOrderError : null}
            amountAppliedByWallet={
              activeStep === 0
                ? walletAppliedAmount
                : checkoutData.amountToUseFromWallet || 0
            }
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default CheckoutPage;
